import { colors } from '@/assets/styles/colors';

// Color thresholds for the "Mi equipo" dashboard cards (ASIS-5404 / REXASIS-003 and the
// rest of the epic). Kept separate from `attendanceThresholds.ts`, which belongs to the
// "Mi asistencia" (personal) dashboard and uses different ranges.

const ATTENDANCE_SITUATION_YELLOW_THRESHOLD = 86;
const ATTENDANCE_SITUATION_GREEN_THRESHOLD = 91;

// "Situación de asistencia" card (ASIS-5404): 0-85% rojo, 86-90% amarillo, 91-100% verde.
export function getAttendanceSituationColor(percentage: number): string {
  if (percentage >= ATTENDANCE_SITUATION_GREEN_THRESHOLD) return colors.feedback.success300;
  if (percentage >= ATTENDANCE_SITUATION_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.error300;
}

// Shared empty state (no supervised employees) across every "Mi equipo" card — not an error.
export const MY_TEAM_EMPTY_STATE_COLOR = colors.grays.neutralCC;

const TARDINESS_SITUATION_YELLOW_THRESHOLD = 8;
const TARDINESS_SITUATION_RED_THRESHOLD = 15;

// "Situación de asistencia" — Atraso (ASIS-5405): 0-7.9% verde, 8-14.9% amarillo, 15-100% rojo.
// INVERTIDO respecto a getAttendanceSituationColor (aquí más alto = peor).
export function getTardinessSituationColor(percentage: number): string {
  if (percentage >= TARDINESS_SITUATION_RED_THRESHOLD) return colors.feedback.error300;
  if (percentage >= TARDINESS_SITUATION_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.success300;
}

const ABSENCE_SITUATION_YELLOW_THRESHOLD = 2;
const ABSENCE_SITUATION_RED_THRESHOLD = 5;

// "Situación de asistencia" — Ausentes (ASIS-5406): 0-1.9% verde, 2-4.9% amarillo, 5-100% rojo.
// INVERTIDO respecto a getAttendanceSituationColor (aquí más alto = peor), igual que Atraso.
export function getAbsenceSituationColor(percentage: number): string {
  if (percentage >= ABSENCE_SITUATION_RED_THRESHOLD) return colors.feedback.error300;
  if (percentage >= ABSENCE_SITUATION_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.success300;
}

// Employee status table (ASIS-5408 / REXASIS-007) — colors the "Estado" column of the
// paginated employee list, one fixed color per status (not a percentage threshold like the
// cards above). Reuses the EXACT same foreground colors already used to paint the status dot
// in the personal "Mi asistencia" dashboard — see STATUS_COLORS in
// `AttendanceSituationCard.tsx` (presente/ausente/advertencia/ausente_justificado) — no new
// hex values introduced here.
const EMPLOYEE_STATUS_TEXT_COLORS: Record<string, string> = {
  ABSENT: colors.feedback.error300,
  LATE: colors.feedback.alert300,
  JUSTIFIED_ABSENCE: colors.important.main,
  PRESENT: colors.feedback.success300,
};

export function getEmployeeStatusTextColor(statusCode: string): string {
  return EMPLOYEE_STATUS_TEXT_COLORS[statusCode] ?? colors.grays.neutral99;
}

const MONTHLY_OVERTIME_YELLOW_THRESHOLD = 76;
const MONTHLY_OVERTIME_GREEN_THRESHOLD = 91;

// "Resumen del mes" — Horas extras (ASIS-5409): 0-75.9% rojo, 76-90.9% amarillo, 91-100% verde.
export function getMonthlyOvertimeColor(percentage: number): string {
  if (percentage >= MONTHLY_OVERTIME_GREEN_THRESHOLD) return colors.feedback.success300;
  if (percentage >= MONTHLY_OVERTIME_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.error300;
}

const MONTHLY_TARDINESS_YELLOW_THRESHOLD = 76;
const MONTHLY_TARDINESS_GREEN_THRESHOLD = 91;

// "Resumen del mes" — Atrasos (ASIS-5410): 0-75.9% rojo, 76-90.9% amarillo, 91-100% verde.
export function getMonthlyTardinessColor(percentage: number): string {
  if (percentage >= MONTHLY_TARDINESS_GREEN_THRESHOLD) return colors.feedback.success300;
  if (percentage >= MONTHLY_TARDINESS_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.error300;
}

const MONTHLY_ABSENCE_YELLOW_THRESHOLD = 76;
const MONTHLY_ABSENCE_GREEN_THRESHOLD = 91;

// "Resumen del mes" — Ausencias (ASIS-5411): 0-75.9% rojo, 76-90.9% amarillo, 91-100% verde.
export function getMonthlyAbsenceColor(percentage: number): string {
  if (percentage >= MONTHLY_ABSENCE_GREEN_THRESHOLD) return colors.feedback.success300;
  if (percentage >= MONTHLY_ABSENCE_YELLOW_THRESHOLD) return colors.feedback.alert300;
  return colors.feedback.error300;
}

// Futuras tarjetas de la épica "Mi equipo" (Marcajes de mes, etc.)
// agregan sus propias funciones de color aquí, junto a las de "Situación de asistencia".
